import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HiOutlineFolderOpen,
  HiOutlineBuildingOffice,
  HiOutlineCurrencyDollar,
  HiOutlineChartBarSquare,
  HiOutlineMapPin,
} from 'react-icons/hi2';
import { fetchDashboardStats } from '../../api/analytics';
import type { DashboardStats } from '../../api/analytics';
import { StatCard } from '../shared/StatCard';
import { ChartCard } from '../charts/ChartCard';
import { CostDonutChart } from '../charts/CostDonutChart';
import { ComparisonBarChart } from '../charts/ComparisonBarChart';
import { buildCostDonutData, COST_COLORS } from '../charts/chartTheme';
import { fmt, fmtCHF, fmtM, fmtCurrency } from '../../utils/format';
import styles from './StatsDetailPage.module.css';

type Metric = 'projects' | 'housing' | 'investment' | 'density';

export function StatsDetailPage() {
  const { metric } = useParams<{ metric: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const validMetric = (['projects', 'housing', 'investment', 'density'] as Metric[]).includes(metric as Metric)
    ? (metric as Metric)
    : 'projects';

  const titles: Record<Metric, string> = {
    projects: t('dashboard:projectsOverview'),
    housing: t('dashboard:housingCapacityTitle'),
    investment: t('dashboard:investmentTitle'),
    density: t('dashboard:densityAnalysis'),
  };

  useEffect(() => {
    fetchDashboardStats()
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className={styles.loading}>{t('common:loading')}</div>;
  if (!stats) return <div className={styles.loading}>{t('common:noDataAvailable')}</div>;

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => navigate('/app')}>
        &larr; {t('common:backToDashboard')}
      </button>
      <h2 className={styles.title}>{titles[validMetric]}</h2>

      {validMetric === 'projects' && <ProjectsView stats={stats} navigate={navigate} />}
      {validMetric === 'housing' && <HousingView stats={stats} />}
      {validMetric === 'investment' && <InvestmentView stats={stats} />}
      {validMetric === 'density' && <DensityView stats={stats} />}
    </div>
  );
}

/* ── Projects View ── */
function ProjectsView({ stats, navigate }: { stats: DashboardStats; navigate: ReturnType<typeof useNavigate> }) {
  const { t } = useTranslation(['dashboard', 'common']);
  return (
    <>
      <div className={styles.statsRow}>
        <StatCard label={t('dashboard:totalProjects')} value={stats.total_projects} icon={<HiOutlineFolderOpen />} />
        <StatCard label={t('common:totalHectares')} value={stats.total_ha > 0 ? fmt(stats.total_ha) : '\u2014'} sub={t('common:ha')} icon={<HiOutlineMapPin />} />
        <StatCard label={t('common:totalLogements')} value={fmt(stats.total_logements)} icon={<HiOutlineBuildingOffice />} />
        <StatCard label={t('common:totalEmplois')} value={fmt(stats.total_emplois)} />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('common:project')}</th>
              <th style={{ textAlign: 'right' }}>{t('common:logements')}</th>
              <th style={{ textAlign: 'right' }}>{t('common:emplois')}</th>
              <th style={{ textAlign: 'right' }}>{t('common:hectares')}</th>
              <th style={{ textAlign: 'right' }}>{t('common:totalCost')}</th>
            </tr>
          </thead>
          <tbody>
            {stats.projects_summary.map((p) => (
              <tr key={p.id} onClick={() => navigate(`/app/projects/${p.id}`)}>
                <td>{p.name}</td>
                <td className={styles.numCell}>{fmt(p.logements)}</td>
                <td className={styles.numCell}>{fmt(p.emplois)}</td>
                <td className={styles.numCell}>{p.ha_total != null ? fmt(p.ha_total) : '\u2014'}</td>
                <td className={styles.numCell}>{p.total_cost > 0 ? fmtCHF(p.total_cost) : '\u2014'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ── Housing View ── */
function HousingView({ stats }: { stats: DashboardStats }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const inhabitants = Math.round(stats.total_logements * 2.3);
  const topProjects = stats.projects_summary
    .slice()
    .sort((a, b) => b.logements - a.logements)
    .slice(0, 8)
    .map((p) => ({
      name: p.name.length > 22 ? p.name.slice(0, 22) + '...' : p.name,
      logements: p.logements,
      emplois: p.emplois,
    }));

  return (
    <>
      <div className={styles.statsRow}>
        <StatCard label={t('common:totalLogements')} value={fmt(stats.total_logements)} icon={<HiOutlineBuildingOffice />} color="var(--color-primary)" />
        <StatCard label={t('common:estInhabitants')} value={fmt(inhabitants)} sub={t('common:xLogements')} />
        <StatCard label={t('common:totalEmplois')} value={fmt(stats.total_emplois)} />
      </div>

      <div className={styles.chartFull}>
        <ChartCard title={t('common:topProjectsByHousing')} subtitle={t('common:logementsAndEmplois')} height={350}>
          <ComparisonBarChart
            data={topProjects}
            metrics={[
              { key: 'logements', label: t('common:logements'), color: '#6366f1' },
              { key: 'emplois', label: t('common:emplois'), color: '#22d3ee' },
            ]}
            layout="vertical"
          />
        </ChartCard>
      </div>
    </>
  );
}

/* ── Investment View ── */
function InvestmentView({ stats }: { stats: DashboardStats }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const donutData = buildCostDonutData(stats.cost_breakdown);
  const breakdownEntries: { name: string; value: number; color: string }[] = [
    { name: t('common:infrastructure'), value: stats.cost_breakdown.infrastructure, color: COST_COLORS.infrastructure },
    { name: t('common:ecoles'), value: stats.cost_breakdown.ecoles, color: COST_COLORS.ecoles },
    { name: t('common:creches'), value: stats.cost_breakdown.creches, color: COST_COLORS.creches },
    { name: t('common:co'), value: stats.cost_breakdown.co, color: COST_COLORS.co },
    { name: t('common:sport'), value: stats.cost_breakdown.sport, color: COST_COLORS.sport },
    { name: t('common:culturel'), value: stats.cost_breakdown.culturel, color: COST_COLORS.culturel },
    { name: t('common:espacesPublics'), value: stats.cost_breakdown.espaces_publics, color: COST_COLORS.espaces_publics },
  ];

  return (
    <>
      <div className={styles.statsRow}>
        <StatCard
          label={t('dashboard:totalInvestment')}
          value={stats.total_cost > 0 ? fmtCHF(stats.total_cost) : '\u2014'}
          icon={<HiOutlineCurrencyDollar />}
          color="var(--color-primary)"
        />
      </div>

      <div className={styles.chartsRow}>
        <ChartCard title={t('common:costDistribution')} subtitle={t('dashboard:byCategory')} height={350}>
          <CostDonutChart
            data={donutData}
            centerValue={stats.total_cost > 0 ? fmtM(stats.total_cost) : undefined}
            centerLabel={stats.total_cost > 0 ? t('common:total') : undefined}
          />
        </ChartCard>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('common:category')}</th>
                <th style={{ textAlign: 'right' }}>{t('common:amount')}</th>
                <th style={{ textAlign: 'right' }}>{t('common:share')}</th>
              </tr>
            </thead>
            <tbody>
              {breakdownEntries.map((entry) => (
                <tr key={entry.name} style={{ cursor: 'default' }}>
                  <td>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: entry.color, marginRight: 8, verticalAlign: 'middle' }} />
                    {entry.name}
                  </td>
                  <td className={styles.numCell}>{entry.value > 0 ? fmtCurrency(entry.value) : '\u2014'}</td>
                  <td className={styles.numCell}>
                    {stats.total_cost > 0 ? `${((entry.value / stats.total_cost) * 100).toFixed(1)}%` : '\u2014'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ── Density View ── */
function DensityView({ stats }: { stats: DashboardStats }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const projectsWithDensity = stats.projects_summary
    .filter((p) => p.ha_total != null && p.ha_total > 0)
    .map((p) => ({
      name: p.name.length > 22 ? p.name.slice(0, 22) + '...' : p.name,
      ha: p.ha_total!,
      logements: p.logements,
      density: p.ha_total! > 0 ? Math.round(p.logements / p.ha_total!) : 0,
    }))
    .sort((a, b) => b.density - a.density)
    .slice(0, 8);

  return (
    <>
      <div className={styles.statsRow}>
        <StatCard
          label={t('dashboard:avgDensity')}
          value={stats.avg_density > 0 ? `${stats.avg_density}` : '\u2014'}
          sub={t('common:logementsPerHa')}
          icon={<HiOutlineChartBarSquare />}
        />
        <StatCard label={t('common:totalHectares')} value={stats.total_ha > 0 ? fmt(stats.total_ha) : '\u2014'} sub={t('common:ha')} />
        <StatCard label={t('common:totalLogements')} value={fmt(stats.total_logements)} />
      </div>

      <div className={styles.chartFull}>
        <ChartCard title={t('common:densityByProject')} subtitle={t('common:logementsPerHectare')} height={350}>
          <ComparisonBarChart
            data={projectsWithDensity}
            metrics={[
              { key: 'density', label: t('common:densityLogHa'), color: '#6366f1' },
            ]}
            layout="vertical"
          />
        </ChartCard>
      </div>
    </>
  );
}
